import { lbBrand } from '@fuse/colors';
import { Button, Icon } from '@material-ui/core';
import AppBar from '@material-ui/core/AppBar';
import Hidden from '@material-ui/core/Hidden';
import { ThemeProvider } from '@material-ui/core/styles';
import Toolbar from '@material-ui/core/Toolbar';
import CropFreeIcon from '@material-ui/icons/CropFree';
import TabletIcon from '@material-ui/icons/Tablet';
import { routesUrl } from 'app/fuse-configs/navigationConfig';
import NavbarMobileToggleButton from 'app/fuse-layouts/shared-components/NavbarMobileToggleButton';
import UserMenu from 'app/fuse-layouts/shared-components/UserMenu';
import { useStores } from 'app/hooks/useStores';
import { loyaltyModelSelector } from 'app/main/apps/loyalties/ducks/loyalty.selector';
import { LoyaltyType } from 'app/main/apps/loyalties/type';
import { webPushSubscriptionSelector } from 'app/main/apps/webPush/ducks/webPush.selector';
import SelectStores from 'app/main/design-system/components/SelectStores/SelectStores';
import { MobileVersionButton } from 'app/main/mobile/shell/MobileVersionButton';
import { selectToolbarTheme } from 'app/store/fuse/settingsSlice';
import clsx from 'clsx';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';
import CreateSaleDialog from './createSale/CreateSaleDialog';
import NotificationButton from './notification/NotificationButton';
import WebPushNotificationDialog from './notification/WebPushNotificationDialog';
import SelectScannerDialog from './selectScanner/SelectScannerDialog';
import SelectTabletTagDialog from './selectTablet/SelectTabletTagDialog';

function ToolbarLayout1() {
  const config = useSelector((state: any) => state.fuse.settings.current.layout.config);
  const toolbarTheme = useSelector(selectToolbarTheme);
  const loyaltyModel = useSelector(loyaltyModelSelector);
  const { data: subscription } = useSelector(webPushSubscriptionSelector);

  const { currentStoreId, setSelectedStoreId, stores } = useStores();

  const [createSaleDialogOpened, setCreateSaleDialogOpened] = useState(false);
  const [openScannerDialog, setOpenScannerDialog] = useState(false);
  const [openTabletDialog, setOpenTabletDialog] = useState(false);
  const { t } = useTranslation();
  const { currentStore } = useStores();

  const hasScannerEnabled = currentStore?.settings.hasScannerEnabled;
  const hasTabletEnabled = currentStore?.settings.hasTabletEnabled;
  const hasManualAmountLoyalty = loyaltyModel && loyaltyModel.loyaltyType === LoyaltyType.MANUAL_AMOUNT;

  const isAdministrationPage = useRouteMatch('/pages/admin/*');
  const isStorePage = useRouteMatch('/pages/stores/*');
  const isScannerMatch = useRouteMatch(routesUrl.scanner);
  const isSatisfactionMatch = useRouteMatch(routesUrl.satisfaction);
  const isMarketingListMatch = useRouteMatch(routesUrl.marketingList);

  if (isScannerMatch) {
    return null;
  }

  return (
    <ThemeProvider theme={toolbarTheme}>
      <AppBar
        id="fuse-toolbar"
        className="relative z-10 flex"
        color="default"
        style={{
          // White top bar (background.paper === #FFFFFF). A 1px #EEEEEE bottom
          // border separates it from the page canvas instead of a drop shadow,
          // and text/icons using color="inherit" pick up #1a1a1a.
          backgroundColor: toolbarTheme.palette.background.paper,
          borderBottom: `1px solid ${lbBrand.border}`,
          color: lbBrand.black,
        }}
        elevation={0}
      >
        <Toolbar className="p-0">
          {config.navbar.display && config.navbar.position === 'left' && (
            <Hidden lgUp>
              <NavbarMobileToggleButton className="w-48 h-48 p-0" />
            </Hidden>
          )}

          <div className="flex justify-between items-center px-16 gap-16">
            {hasManualAmountLoyalty && stores.length > 0 ? (
              <Button
                onClick={() => {
                  setCreateSaleDialogOpened(true);
                }}
                className="normal-case mr-16"
                variant="contained"
                color="primary"
                endIcon={<Icon>add</Icon>}
              >
                <span className="hidden md:block">{t('dialogs.createSaleDialog.createSale').toUpperCase()}</span>
                <span className="block md:hidden">{t('dialogs.createSaleDialog.createSaleMobile').toUpperCase()}</span>
              </Button>
            ) : null}

            {hasScannerEnabled && stores.length > 0 && (
              <Button
                onClick={() => {
                  setOpenScannerDialog(true);
                }}
                className="normal-case"
                variant="outlined"
                color="primary"
                endIcon={<CropFreeIcon />}
              >
                <span className="hidden md:block">{t('dialogs.selectScanner.button').toUpperCase()}</span>
              </Button>
            )}

            {hasTabletEnabled && stores.length > 0 && (
              <Button
                onClick={() => {
                  setOpenTabletDialog(true);
                }}
                className="normal-case"
                variant="outlined"
                color="primary"
                endIcon={<TabletIcon />}
              >
                <span className="hidden md:block">{t('dialogs.tablet.button').toUpperCase()}</span>
              </Button>
            )}
          </div>

          {!isAdministrationPage &&
            !isStorePage &&
            !isSatisfactionMatch &&
            !isMarketingListMatch &&
            stores.length > 0 && (
              <div className="flex flex-1 justify-center items-center px-16">
                <SelectStores
                  size="small"
                  value={currentStoreId || ''}
                  label={t('components.header.selectStore')}
                  variant="outlined"
                  className={clsx('h-40 md:min-w-320')}
                  onSelect={storeId => {
                    if (storeId) {
                      setSelectedStoreId(storeId);
                    }
                  }}
                />
              </div>
            )}

          <div className="flex items-center ml-auto">
            <MobileVersionButton />
            {!isAdministrationPage && <NotificationButton />}
            <UserMenu />
          </div>

          {config.navbar.display && config.navbar.position === 'right' && (
            <Hidden lgUp>
              <NavbarMobileToggleButton />
            </Hidden>
          )}
        </Toolbar>
      </AppBar>
      <WebPushNotificationDialog subscription={subscription || undefined} />
      {hasManualAmountLoyalty && (
        <CreateSaleDialog open={createSaleDialogOpened} onClose={() => setCreateSaleDialogOpened(false)} />
      )}
      {hasScannerEnabled && (
        <SelectScannerDialog open={openScannerDialog} onClose={() => setOpenScannerDialog(false)} />
      )}
      {hasTabletEnabled && <SelectTabletTagDialog open={openTabletDialog} onClose={() => setOpenTabletDialog(false)} />}
      {}
    </ThemeProvider>
  );
}

export default React.memo(ToolbarLayout1);
